import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasAccess } from "@/lib/roles";
import { ShieldCheck, ArrowRight, CheckCircle2 } from "lucide-react";

export default function GlobalStandardsQueue() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("awaiting_global_review");

  const load = useCallback(() => {
    // Backend expects `status_` (with trailing underscore). Also apply a
    // client-side filter as a safety net.
    const p = tab === "all" ? "" : `?status_=${tab}`;
    api.get(`/display_checks${p}`).then(({ data }) => {
      const filtered = tab === "all" ? data : (data || []).filter((d) => d.status === tab);
      setItems(filtered);
    }).catch((e) => toast.error(formatApiError(e)));
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  if (!hasAccess(user?.role, ["admin", "global_standards"])) {
    return <div className="hos-card p-6 text-center text-[color:var(--text-secondary)]">Global Standards access required.</div>;
  }

  return (
    <div className="space-y-6" data-testid="global-standards-queue">
      <div>
        <div className="label-caps">Global Standards</div>
        <h1 className="font-heading text-3xl sm:text-4xl flex items-center gap-2">
          <ShieldCheck size={28} className="text-[color:var(--gold)]"/> Approval Queue
        </h1>
        <div className="text-[color:var(--text-secondary)] text-sm mt-1">Review GM-approved Display Checks for final approval</div>
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { k: "awaiting_global_review", l: "Awaiting Review" },
          { k: "returned_to_gm", l: "Returned to GM" },
          { k: "completed", l: "Final Approved" },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)}
                  className={`px-3 py-2 rounded-md text-sm border ${tab === t.k ? "border-[color:var(--gold)] text-[color:var(--gold)]" : "border-[color:var(--border)] text-[color:var(--text-secondary)]"}`}
                  data-testid={`gs-tab-${t.k}`}>
            {t.l}
          </button>
        ))}
      </div>

      <div className="hos-card overflow-hidden">
        {items.length === 0 && (
          <div className="p-10 text-center text-[color:var(--text-secondary)]">No Display Checks in this state.</div>
        )}
        {items.length > 0 && (
          <ul className="divide-y divide-[color:var(--border)]">
            {items.map((dc) => {
              const zones = dc.zones || [];
              const done = zones.filter((z) => ["completed", "corrected"].includes(z.status)).length;
              const photoCount = zones.reduce((n, z) => n + ((z.photos || []).length), 0);
              const areaCodes = new Set();
              zones.forEach((z) => (z.photos || []).forEach((p) => p.display_area_code && areaCodes.add(p.display_area_code)));
              return (
                <li key={dc.id}>
                  <Link to={`/display/${dc.id}`} className="block p-4 hover:bg-[color:var(--bg-surface-2)]/40" data-testid={`gs-row-${dc.check_number}`}>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="min-w-0">
                        <div className="font-heading text-lg">{dc.check_number} · {dc.store_name}</div>
                        <div className="text-xs text-[color:var(--text-secondary)]">
                          Manager: {dc.manager_name || "—"}
                          {dc.gm_approved_by_name ? ` · GM: ${dc.gm_approved_by_name}` : ""}
                          {" · "}{done}/{zones.length} zones · {photoCount} photos
                          {areaCodes.size ? ` · ${areaCodes.size} areas` : ""}
                        </div>
                        <div className="text-[10px] text-[color:var(--text-secondary)] mt-0.5">
                          {dc.gm_approved_at ? `GM approved ${new Date(dc.gm_approved_at).toLocaleString()}` : ""}
                          {dc.global_return_reason ? ` · Returned: ${dc.global_return_reason.slice(0, 80)}` : ""}
                        </div>
                      </div>
                      <span className="text-[color:var(--gold)] flex items-center gap-1 text-sm">Review <ArrowRight size={14}/></span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
